import {
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
  signal
} from '@angular/core'
import {FormArray, FormControl, FormGroup, Validators} from '@angular/forms'
import {MatButton} from '@angular/material/button'
import {MatCard, MatCardContent} from '@angular/material/card'
import {
  MatStep,
  MatStepLabel,
  MatStepper,
  MatStepperNext,
  MatStepperPrevious
} from '@angular/material/stepper'
import {
  phoneValidator,
  StepperScrollDirective
} from '@sparbanken-syd/sparbanken-syd-theme'
import {OrientationService} from '../../services/orientation.service'
import {RouteService} from '../../services/route.service'
import {SavingsAccountService} from '../../services/savings-account.service'
import {WelcomeComponent} from '../1-welcome/welcome.component'
import {ApplicantsComponent} from '../2-applicants/applicants.component'
import {KycComponent} from '../3-kyc/kyc.component'
import {SummaryComponent} from '../4-summary/summary.component'

// Constants in minutes
const TOTAL_MINUTES = 30
const WARNING_THRESHOLD = 5

@Component({
  selector: 'spb-form',
  templateUrl: './form.component.html',
  styleUrl: './form.component.scss',
  imports: [
    MatStepper,
    MatStep,
    MatStepLabel,
    WelcomeComponent,
    ApplicantsComponent,
    KycComponent,
    SummaryComponent,
    StepperScrollDirective,
    MatStepperPrevious,
    MatButton,
    MatCard,
    MatCardContent,
    MatStepperNext
  ]
})
export class FormComponent implements OnInit, OnDestroy {
  public welcomeForm = new FormGroup({
    terms: new FormControl<boolean>(false, {
      nonNullable: true,
      validators: [Validators.requiredTrue]
    })
  })

  public applicantsForm = new FormGroup({
    personalNumber: new FormControl<string | null>(null, {nonNullable: true}),
    name: new FormControl<string | null>(null, {nonNullable: true}),
    streetAdress: new FormControl<string | null>(null, {nonNullable: true}),
    streetNumber: new FormControl<string | null>(null, {nonNullable: true}),
    postalCode: new FormControl<string | null>(null, {nonNullable: true}),
    city: new FormControl<string | null>(null, {nonNullable: true}),
    countryCode: new FormControl<string | null>(null, {
      nonNullable: true,
      validators: [Validators.required]
    }),
    phone: new FormControl<string>('', {
      nonNullable: true,
      validators: [phoneValidator(), Validators.required]
    }),
    email: new FormControl<string | null>(null, {
      nonNullable: true,
      validators: [Validators.email, Validators.required]
    }),
    sparFailed: new FormControl<boolean>(false, {nonNullable: true}),
    hasMultipleAddresses: new FormControl<boolean>(false, {nonNullable: true})
  })

  public birthForm = new FormGroup({
    birthCountry: new FormControl<string | null>(null, {
      nonNullable: true,
      validators: [Validators.required]
    }),
    birthCity: new FormControl<string | null>(null, {
      nonNullable: true,
      validators: [Validators.required]
    }),
    citizenship: new FormArray([
      new FormControl(null, {
        nonNullable: true,
        validators: [Validators.required]
      })
    ])
  })

  public primaryResidenceForm = new FormGroup({
    primaryResidence: new FormControl<string | null>(null, {
      nonNullable: true,
      validators: [Validators.required]
    })
  })

  public greenCardForm = new FormGroup({
    greenCard: new FormControl<boolean | null>(null, {
      nonNullable: true,
      validators: [Validators.required]
    })
  })

  public americanCitizenshipForm = new FormGroup({
    americanCitizenship: new FormControl<boolean | null>(null, {
      nonNullable: true,
      validators: [Validators.required]
    })
  })

  public taxResidencyForm = new FormGroup({
    taxResidencySwedish: new FormControl<boolean | null>(null, {
      nonNullable: true,
      validators: [Validators.required]
    }),
    taxResidencyNotSwedish: new FormControl<boolean | null>(null, {
      nonNullable: true,
      validators: [Validators.required]
    }),
    taxResidencyInfo: new FormArray([]),
    onlySwedenTaxCheckbox: new FormControl<boolean | null>(null, {nonNullable: true}),
    notUsaCitizenCheckbox: new FormControl<boolean | null>(null, {nonNullable: true})
  })

  public pepForm = new FormGroup({
    pepStatus: new FormControl<string | null>(null, {
      nonNullable: true,
      validators: [Validators.required]
    }),
    pepTitle: new FormControl<string | null>(null, {
      nonNullable: true,
      validators: [Validators.required]
    }),
    pepPosition: new FormControl<string | null>(null, {
      nonNullable: true,
      validators: [Validators.required]
    }),
    pepCountry: new FormControl<string | null>(null, {
      nonNullable: true,
      validators: [Validators.required]
    }),
    pepRelation: new FormControl<string | null>(null, {
      nonNullable: true,
      validators: [Validators.required]
    }),
    pepOtherRelation: new FormControl<boolean | null>(null, {nonNullable: true}),
    pepOtherRelationInfo: new FormControl<string | null>(null, {nonNullable: true}),
    pepOtherWork: new FormControl<boolean | null>(null, {nonNullable: true}),
    pepOtherWorkInfo: new FormControl<string | null>(null, {nonNullable: true})
  })

  public bankRelationshipForm = new FormGroup({
    openSavingsAccount: new FormControl<boolean>(false, {
      nonNullable: true,
      validators: [Validators.requiredTrue]
    }),
    openHorseAccount: new FormControl<boolean>(false, {
      nonNullable: true
    }),
    openFixedRateAccount: new FormControl<boolean>(false, {nonNullable: true}),
    existingSavingsAccount: new FormControl<boolean>(false, {nonNullable: true}),
    existingSavingsAccountNumber: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.pattern(/^\d{10,}$/)]
    }),
    interestRunningTime: new FormControl<string | null>(null, {nonNullable: true}),
    interestPlacementAmount: new FormControl<string | null>(null, {nonNullable: true}),
    wantToBeContacted: new FormControl<boolean | null>(null, {
      nonNullable: true,
      validators: [Validators.required]
    })
  })

  public incomeForm = new FormGroup({
    salary: new FormControl<number | null>(null, {nonNullable: true}),
    pension: new FormControl<number | null>(null, {nonNullable: true}),
    horse: new FormControl<number | null>(null, {nonNullable: true}),
    benefits: new FormControl<number | null>(null, {nonNullable: true}),
    studentAid: new FormControl<number | null>(null, {nonNullable: true}),
    soleProprietorship: new FormControl<number | null>(null, {nonNullable: true}),
    capital: new FormControl<number | null>(null, {nonNullable: true}),
    other: new FormControl<number | null>(null, {nonNullable: true})
  })

  public savingsForm = new FormGroup({
    savingsTotal: new FormControl<number | null>(null, {
      nonNullable: true,
      validators: [Validators.required]
    }),
    savingsSource: new FormArray([
      new FormControl(),
      new FormControl(),
      new FormControl(),
      new FormControl(),
      new FormControl()
    ])
  })

  public kycForm = new FormGroup({
    birthForm: this.birthForm,
    primaryResidenceForm: this.primaryResidenceForm,
    greenCardForm: this.greenCardForm,
    americanCitizenshipForm: this.americanCitizenshipForm,
    taxResidencyForm: this.taxResidencyForm,
    pepForm: this.pepForm,
    bankRelationshipForm: this.bankRelationshipForm,
    incomeForm: this.incomeForm,
    savingsForm: this.savingsForm
  })

  public savingsAccountForm = new FormGroup({
    id: new FormControl<string>('', {nonNullable: true}),
    isHorseApplication: new FormControl<boolean>(false, {nonNullable: true}),
    welcomeForm: this.welcomeForm,
    applicantData: this.applicantsForm,
    kycForm: this.kycForm
  })

  public minutesLeft = signal(TOTAL_MINUTES)
  public showExtend = computed(() => this.minutesLeft() <= WARNING_THRESHOLD)

  protected readonly savingsAccountService = inject(SavingsAccountService)
  protected readonly orientationService = inject(OrientationService)
  protected readonly routeService = inject(RouteService)

  private intervalId: ReturnType<typeof setInterval> | null = null

  public extendTimer() {
    this.minutesLeft.set(TOTAL_MINUTES)
  }

  public ngOnInit() {
    if (this.routeService.onHorseRoute()) {
      this.savingsAccountForm.patchValue({isHorseApplication: true})
    }
    const sub = this.savingsAccountService.applicant().personalNumber

    this.savingsAccountService.getSpar(sub).subscribe({
      next: r => {
        this.savingsAccountService.updateApplicantData(sub, r)
      },
      complete: () => {
        // Executes when the subscription is complete, regardless of success or failure.
        this.applicantsForm.patchValue(this.savingsAccountService.applicant())

        // Track successful applicant logins upon receiving a response from SPAR.
        this.savingsAccountService.saveTrack().subscribe()
      },
      error: () => {
        this.savingsAccountService.updateApplicantData(sub, undefined)
        this.applicantsForm.controls.sparFailed.setValue(true)
        this.applicantsForm.controls.personalNumber.setValue(sub)
        this.applicantsForm.controls.countryCode.setValue('+46')
      }
    })

    // Set up a timer to reset the application after x minutes
    this.startTimer()
  }

  public ngOnDestroy() {
    clearInterval(this.intervalId!)
  }

  private startTimer() {

    this.intervalId = setInterval(() => {
      this.minutesLeft.update(m => m - 1)

      if (this.minutesLeft() <= 0) {
        clearInterval(this.intervalId!)
        this.resetApplication()
      }
    }, 60000)
  }

  private resetApplication() {
    this.savingsAccountService.sessionTimeout.set(true)
    this.savingsAccountForm.reset()
    this.savingsAccountService.applicant.set({
      name: '',
      personalNumber: '',
      countryCode: '',
      email: '',
      phone: '',
      city: '',
      postalCode: '',
      streetNumber: '',
      streetAdress: ''
    })
  }
}